import React from "react";
import {
  Send,
  Eye,
  CheckCircle2,
  XCircle,
  CreditCard,
  Pencil,
  FileText,
} from "lucide-react";

/**
 * ActivityTimeline — vertical event feed per quote/lead.
 *
 * Sources events from columns already on the quotes row (no extra fetch):
 *   - created_at       → "Quote drafted"
 *   - sent_at          → "Quote sent to customer"
 *   - viewed_at        → "Customer first opened the quote"
 *   - view_count > 1   → "Customer viewed N times" (last_viewed_at)
 *   - accepted_at      → "Customer accepted"
 *   - declined_at      → "Customer declined"
 *   - deposit_paid_at  → "Deposit paid"
 *
 * Renders newest-first. Each event shows an icon, label, and a relative
 * timestamp ("2h ago") with a tooltip showing the precise time.
 *
 * @param {Object} props
 * @param {Object} props.quote - the quotes row (with the columns above)
 */
export default function ActivityTimeline({ quote }) {
  if (!quote) return null;

  const events = [];

  if (quote.created_at) {
    events.push({
      key: "created",
      icon: Pencil,
      label: "Quote drafted",
      at: quote.created_at,
      tone: "neutral",
    });
  }
  if (quote.sent_at) {
    events.push({
      key: "sent",
      icon: Send,
      label: "Quote sent to customer",
      at: quote.sent_at,
      tone: "primary",
    });
  }
  if (quote.viewed_at) {
    events.push({
      key: "viewed",
      icon: Eye,
      label: "Customer first opened the quote",
      at: quote.viewed_at,
      tone: "info",
    });
  }
  if (
    quote.view_count > 1 &&
    quote.last_viewed_at &&
    quote.last_viewed_at !== quote.viewed_at
  ) {
    events.push({
      key: "last-viewed",
      icon: Eye,
      label: `Customer viewed ${quote.view_count} times in total`,
      at: quote.last_viewed_at,
      tone: "info",
    });
  }
  if (quote.accepted_at || quote.customer_status === "accepted") {
    events.push({
      key: "accepted",
      icon: CheckCircle2,
      label: "Customer accepted",
      at: quote.accepted_at || quote.customer_responded_at,
      tone: "success",
    });
  }
  if (quote.declined_at || quote.customer_status === "declined") {
    events.push({
      key: "declined",
      icon: XCircle,
      label: "Customer declined",
      at: quote.declined_at || quote.customer_responded_at,
      tone: "error",
    });
  }
  if (quote.deposit_paid && quote.deposit_paid_at) {
    events.push({
      key: "deposit",
      icon: CreditCard,
      label: "Deposit paid",
      at: quote.deposit_paid_at,
      tone: "success",
    });
  }

  // Sort newest-first.
  events.sort((a, b) => {
    const ta = a.at ? new Date(a.at).getTime() : 0;
    const tb = b.at ? new Date(b.at).getTime() : 0;
    return tb - ta;
  });

  if (events.length === 0) {
    return (
      <div
        className="muted small"
        style={{
          padding: "10px 12px",
          border: "1px dashed var(--border, rgba(140,100,80,0.18))",
          borderRadius: "10px",
          textAlign: "center",
        }}
      >
        No activity yet. Save and send the quote to start tracking events.
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "12px 14px",
        border: "1px solid var(--border, rgba(140,100,80,0.14))",
        borderRadius: "12px",
        background: "var(--surface-soft, #faf5f2)",
      }}
    >
      <div
        style={{
          fontSize: "0.75rem",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--text-soft, #6b5448)",
          marginBottom: "2px",
        }}
      >
        <FileText
          size={12}
          style={{ verticalAlign: "-1px", marginRight: "6px" }}
        />
        Activity
      </div>
      {events.map((e) => (
        <ActivityRow key={e.key} event={e} />
      ))}
    </div>
  );
}

// ── Single row ──────────────────────────────────────────────────────────────
function ActivityRow({ event }) {
  const Icon = event.icon;
  const TONE = {
    success: { bg: "rgba(34,197,94,0.12)", color: "#15803d" },
    error: { bg: "rgba(220,38,38,0.10)", color: "#b91c1c" },
    primary: { bg: "rgba(255, 138, 0,0.12)", color: "#CC6E00" },
    info: { bg: "rgba(59,130,246,0.10)", color: "#1d4ed8" },
    neutral: { bg: "rgba(0,0,0,0.05)", color: "var(--text-soft, #6b5448)" },
  };
  const t = TONE[event.tone] || TONE.neutral;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
      }}
      title={event.at ? new Date(event.at).toLocaleString() : undefined}
    >
      <div
        style={{
          width: "26px",
          height: "26px",
          borderRadius: "50%",
          background: t.bg,
          color: t.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          marginTop: "1px",
        }}
        aria-hidden="true"
      >
        <Icon size={14} strokeWidth={2.4} />
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "baseline",
          gap: "8px",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontSize: "0.875rem",
            color: "var(--text)",
            lineHeight: 1.4,
          }}
        >
          {event.label}
        </div>
        <div
          className="muted small"
          style={{
            fontSize: "0.75rem",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {timeAgo(event.at)}
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}
